import { format, isToday, isYesterday } from 'date-fns';
import React, { useMemo } from 'react';
import { Icon } from 'shared/components/Icon';
import './style.less';
import { VisitorOverviewItem } from 'browser/components/Dashboard/VisitorFocused/VisitorOverview';
import { VisitorActivity } from 'sb-lib/types';
import { ActiveVisitor } from 'types/client';
import clsx from 'clsx';
import { SpotlightActivityCallItem } from '../AcitivityCallItem';
import { CallDisposition, CallTaskStatus, ContactList, SourceType } from 'types/outbound';

export type SpotlightActivity = Omit<VisitorActivity, 'data'> & {
  data: {
    notes?: string;
    dialTask: {
      callDuration?: number;
      status: CallTaskStatus;
      recordingUrl?: string;
      sourceId: number | null;
      sourceType: SourceType;
      externalServiceDispositionId?: string;
    };
  };
};

export type SpotlightActivityCallProps = {
  apolloCallDispositions: CallDisposition[];
  apolloContactLists: ContactList[];
  hubspotCallDispositions: CallDisposition[];
  hubspotContactLists: ContactList[];
  outreachCallDispositions: CallDisposition[];
  outreachContactLists: ContactList[];
  salesforceCallDispositions: CallDisposition[];
  salesforceContactLists: ContactList[];
  salesloftCallDispositions: CallDisposition[];
  salesloftContactLists: ContactList[];
  servicebellCallDispositions: CallDisposition[];
  servicebellContactLists: ContactList[];
  externalServiceDispositionId?: string;
};

export const SpotlightAccountTabsActivityGroup: React.FC<
  {
    label: string;
    items: VisitorActivity[];
  } & SpotlightActivityCallProps
> = ({ label, items, ...callActivityProps }) => {
  const formattedLabel = useMemo(() => {
    const keyDate = new Date(label);
    const formatted = format(keyDate, 'MMMM do');

    if (isToday(keyDate)) {
      return `Today, ${formatted}`;
    } else if (isYesterday(keyDate)) {
      return `Yesterday, ${formatted}`;
    } else {
      return formatted;
    }
  }, [label]);

  if (!items.length) {
    return <></>;
  }

  return (
    <div className="SpotlightAccountTabsActivityGroup">
      <div className="SpotlightAccountTabsActivityGroup__label">
        <div
          className={clsx(
            'SpotlightAccountTabsActivityGroup__icon',
            isToday(new Date(label)) && 'SpotlightAccountTabsActivityGroup__icon--today',
            'SpotlightAccountTabsActivityGroup__icon',
          )}
        >
          <Icon icon="calendar2" width={16} height={16} />
        </div>
        {formattedLabel}
      </div>

      {items.map((item, i) => {
        if (item.type === 'CALL') {
          return (
            <SpotlightActivityCallItem
              key={`${item.id}-${i}`}
              activity={item as SpotlightActivity}
              {...callActivityProps}
            />
          );
        }

        return (
          <VisitorOverviewItem
            key={`${item.id}-${i}`}
            activity={item}
            activities={items as VisitorActivity[]}
            visitor={
              {
                id: item.visitorId,
                displayName: item.visitorDisplayName ?? 'Unknown',
              } as ActiveVisitor
            }
          />
        );
      })}
    </div>
  );
};
